import React, { useMemo, useState } from 'react';
import { Camera, RefreshCw, Save, Upload, User } from 'lucide-react';
import { BACKEND_URL } from '../../firebase/config';
import { getDisplayFileUrl } from '../../utils/mediaUrls';

const facultyIdentity = (faculty = {}) => (
  faculty.loginId || faculty.username || faculty.email || faculty.id || ''
);

const getSessionToken = () => {
  const raw = window.sessionStorage.getItem('userSession') || window.sessionStorage.getItem('adminSession');
  if (!raw) return '';

  try {
    return JSON.parse(raw)?.sessionToken || '';
  } catch (error) {
    console.warn('Could not read session token.', error);
    return '';
  }
};

const updateStoredFacultySession = (updates = {}) => {
  const raw = window.sessionStorage.getItem('userSession');
  if (!raw) return;

  try {
    const current = JSON.parse(raw);
    window.sessionStorage.setItem('userSession', JSON.stringify({
      ...current,
      ...updates,
      sessionToken: current.sessionToken,
      uid: current.uid
    }));
  } catch (error) {
    console.warn('Could not refresh faculty session.', error);
  }
};

const profileFromUser = (userData = {}) => ({
  name: userData.name || '',
  username: userData.username || userData.loginId || '',
  email: userData.email || '',
  designation: userData.designation || 'Faculty',
  phone: userData.phone || '',
  department: userData.department || 'Engineering Sciences & Humanities',
  password: '',
  photoUrl: userData.photoUrl || '',
  photoPath: userData.photoPath || ''
});

const FacultyProfileWidget = ({ userData = {} }) => {
  const [profile, setProfile] = useState(profileFromUser(userData));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const identity = useMemo(() => facultyIdentity(userData) || profile.username || profile.email, [profile.email, profile.username, userData]);
  const photoUrl = getDisplayFileUrl(profile.photoPath || profile.photoUrl || '');

  const updateProfile = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
    setMessage('');
    setError('');
  };

  const uploadPhoto = async (file) => {
    if (!file) return;

    setUploading(true);
    setError('');
    setMessage('');

    try {
      const form = new FormData();
      form.append('file', file);
      form.append('facultyId', identity || 'faculty');
      form.append('username', profile.username);
      form.append('email', profile.email);
      form.append('uploadedBy', profile.name || identity || 'Faculty');
      form.append('oldPhotoPath', profile.photoPath || '');
      form.append('oldPhotoUrl', profile.photoUrl || '');

      const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/upload/faculty-photo`, {
        method: 'POST',
        body: form
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Photo upload failed.');
      }

      setProfile((current) => ({
        ...current,
        photoUrl: result.photoUrl || result.fileUrl || '',
        photoPath: result.photoPath || result.filePath || ''
      }));
      setMessage('Photo uploaded. Click Save Profile to keep it with your profile.');
    } catch (uploadError) {
      console.error('Faculty photo upload failed:', uploadError);
      setError(uploadError.message || 'Photo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    const token = getSessionToken();
    if (!token) {
      setError('Session expired. Please log out and sign in again.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        ...profile,
        username: profile.username || userData.loginId || userData.username,
        loginId: profile.username || userData.loginId || userData.username,
        email: profile.email || userData.email,
        roles: Array.isArray(userData.assignments) ? userData.assignments : []
      };

      if (!payload.password) {
        delete payload.password;
      }

      const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/auth/faculty`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Could not save profile.');
      }

      updateStoredFacultySession({
        ...result.faculty,
        assignments: result.assignments || userData.assignments || []
      });
      setProfile((current) => ({ ...current, password: '' }));
      setMessage('Profile saved. Reopen the dashboard to refresh the header name/photo everywhere.');
    } catch (saveError) {
      console.error('Faculty profile save failed:', saveError);
      setError(saveError.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-5 md:flex-row">
        <div className="flex w-full flex-col items-center rounded-lg border border-gray-200 bg-gray-50 p-4 md:w-56">
          <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-white">
            {photoUrl ? (
              <img src={photoUrl} alt="Faculty" className="h-full w-full object-cover" />
            ) : (
              <User className="h-14 w-14 text-gray-400" />
            )}
          </div>
          <label className="mt-4 inline-flex cursor-pointer items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            {uploading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload Photo
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(event) => uploadPhoto(event.target.files?.[0])}
              disabled={uploading}
            />
          </label>
          <p className="mt-2 text-center text-xs text-gray-500">JPG or PNG, up to 5 MB</p>
        </div>

        <div className="grid flex-1 gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Name</span>
            <input
              value={profile.name}
              onChange={(event) => updateProfile('name', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Faculty ID</span>
            <input
              value={profile.username}
              readOnly
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Email</span>
            <input
              type="email"
              value={profile.email}
              readOnly
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Designation</span>
            <input
              value={profile.designation}
              onChange={(event) => updateProfile('designation', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Phone</span>
            <input
              value={profile.phone}
              onChange={(event) => updateProfile('phone', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">New Password</span>
            <input
              type="password"
              value={profile.password}
              onChange={(event) => updateProfile('password', event.target.value)}
              placeholder="Leave blank to keep current"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Department</span>
            <input
              value={profile.department}
              onChange={(event) => updateProfile('department', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {message && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>
      )}

      <button
        type="button"
        onClick={saveProfile}
        disabled={saving || uploading}
        className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
      >
        {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Save Profile
      </button>

      <div className="flex items-start rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <Camera className="mr-2 mt-0.5 h-4 w-4" />
        Photos are stored in Google Drive; the database backup keeps the photo references.
      </div>
    </div>
  );
};

export default FacultyProfileWidget;
